import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import type { BlogPost, BlogPostInput } from '@/types/blog';

const BLOG_COLLECTION = 'blogPosts';

export const blogService = {
  // Create a new blog post
  async createPost(postData: BlogPostInput): Promise<string> {
    const now = Date.now();
    const docRef = await addDoc(collection(db, BLOG_COLLECTION), {
      ...postData,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  },

  // Update an existing blog post
  async updatePost(id: string, postData: Partial<BlogPostInput>): Promise<void> {
    const docRef = doc(db, BLOG_COLLECTION, id);
    await updateDoc(docRef, {
      ...postData,
      updatedAt: Date.now(),
    });
  },

  // Delete a blog post
  async deletePost(id: string): Promise<void> {
    const docRef = doc(db, BLOG_COLLECTION, id);
    await deleteDoc(docRef);
  },

  // Get a single blog post by ID
  async getPost(id: string): Promise<BlogPost | null> {
    const docRef = doc(db, BLOG_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as BlogPost;
    }
    return null;
  },

  // Get all posts (alias for backward compatibility)
  async getPosts(): Promise<BlogPost[]> {
    return this.getAllPosts();
  },

  // Get all blog posts (for admin)
  async getAllPosts(): Promise<BlogPost[]> {
    const q = query(collection(db, BLOG_COLLECTION), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as BlogPost));
  },

  // Get published blog posts (for public)
  async getPublishedPosts(): Promise<BlogPost[]> {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('published', '==', true),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as BlogPost));
  },

  // Get post by slug (optimized for language switching)
  async getPostBySlug(slug: string, language: 'en' | 'fr'): Promise<BlogPost | null> {
    // First try to find by the new format (slug.en or slug.fr)
    const slugField = `slug.${language}`;
    const q1 = query(
      collection(db, BLOG_COLLECTION),
      where(slugField, '==', slug)
    );
    const snapshot1 = await getDocs(q1);

    if (!snapshot1.empty) {
      const doc = snapshot1.docs[0];
      return { id: doc.id, ...doc.data() } as BlogPost;
    }

    // Fallback: try to find by old format (slug as string)
    const q2 = query(
      collection(db, BLOG_COLLECTION),
      where('slug', '==', slug)
    );
    const snapshot2 = await getDocs(q2);

    if (!snapshot2.empty) {
      const doc = snapshot2.docs[0];
      return { id: doc.id, ...doc.data() } as BlogPost;
    }

    return null;
  },

  // Upload image to Firebase Storage
  async uploadImage(file: File): Promise<string> {
    const timestamp = Date.now();
    const fileName = `blog-images/${timestamp}-${file.name}`;
    const storageRef = ref(storage, fileName);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  },
};
